import {useQuery, useMutation} from '@apollo/client'
import {Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {GET_USER_EMAIL_SETTINGS, GET_RESOLVED_EMAIL_SETTINGS} from '../../../gql/queries/emailSettingsQuery'
import {SEND_TEST_EMAIL, UPDATE_USER_EMAIL_SETTINGS} from '../../../gql/mutations/emailSettingsMutations'
import {EmailSettingsForm, IEmailSettingsValues} from '../../shared/EmailSettingsForm'

interface Props {
  userId: string
}

export const UserEmailSettingsTab: React.FC<Props> = ({userId}) => {
  const {data, loading, refetch} = useQuery(GET_USER_EMAIL_SETTINGS, {
    variables: {userId},
    fetchPolicy: 'network-only',
  })
  const {data: resolvedData, refetch: refetchResolved} = useQuery(GET_RESOLVED_EMAIL_SETTINGS, {
    variables: {userId},
    fetchPolicy: 'network-only',
  })
  const [updateUserEmailSettings, {loading: saving}] = useMutation(UPDATE_USER_EMAIL_SETTINGS)
  const [sendTestEmail, {loading: sendingTestEmail}] = useMutation(SEND_TEST_EMAIL)

  const settings = data?.userEmailSettings
  const resolved = resolvedData?.resolvedEmailSettings

  const handleSendTestEmail = async (toEmail: string) => {
    try {
      const res = await sendTestEmail({variables: {toEmail, userId}})
      const result = res.data?.sendTestEmail
      if (!result?.success) {
        toast.error(result?.errors?.map((e: any) => e.message).join('\n') || 'Error sending test email')
        return
      }
      toast.success(`Test email sent to ${toEmail}`)
    } catch (err: any) {
      toast.error(err.message || 'Error sending test email')
    }
  }

  const handleSave = async (input: IEmailSettingsValues) => {
    try {
      const res = await updateUserEmailSettings({variables: {userId, input}})
      const result = res.data?.updateUserEmailSettings
      if (!result?.success) {
        toast.error(result?.errors?.map((e: any) => e.message).join('\n') || 'Error saving Email Settings')
        return
      }
      toast.success('Email Settings saved')
      refetch()
      refetchResolved()
    } catch (err: any) {
      toast.error(err.message || 'Error saving Email Settings')
    }
  }

  if (loading) {
    return (
      <div className='text-center py-6'>
        <Spinner animation='border' size='sm' />
      </div>
    )
  }

  return (
    <div>
      <p className='text-muted mb-4' style={{fontSize: '0.85rem'}}>
        Personal sender and signature for emails sent by this user. Fields without an override inherit
        from the Organization or the system.
      </p>
      <EmailSettingsForm
        values={settings}
        resolved={resolved}
        showInheritance
        saving={saving}
        onSave={handleSave}
        onSendTestEmail={handleSendTestEmail}
        sendingTestEmail={sendingTestEmail}
      />
    </div>
  )
}
